import CloseIcon from "@mui/icons-material/Close";
import { useEffect, useState } from "react";

import { FEATURE_ACCESS_UI_ENVIRONMENTS } from "./featureAccessEnvironments";
import {
  type FeatureAccessDefinition,
  isFeatureReady,
} from "./featureAccessRegistry";
import {
  FeatureAccessRow,
  type FeatureConfirmRequest,
  type FeatureConsoleFeedback,
} from "./FeatureAccessRow";
import "./featureFlagsConsole.css";

import { VNextButton, VNextDialog, VNextNotice } from "@/vnext/primitives";

interface FeatureAccessTableProps {
  features: FeatureAccessDefinition[];
  readiness: Record<string, boolean>;
  canManage: boolean;
  isDevStaff: boolean;
}

/**
 * Feature rollout as a table: one row per feature (name, dev-staff readiness
 * toggle, on-for-everyone toggle), each expandable to the per-organisation
 * access list. Owns the shared feedback banner and the single confirmation
 * dialog the rows route their all-organisations changes through.
 */
export const FeatureAccessTable = ({
  features,
  readiness,
  canManage,
  isDevStaff,
}: FeatureAccessTableProps) => {
  const [feedback, setFeedback] = useState<FeatureConsoleFeedback | null>(null);
  const [confirm, setConfirm] = useState<FeatureConfirmRequest | null>(null);
  // Label of the feature a blocked rollout attempt was made on, or null when
  // the "not ready" modal is closed. A feature that is not ready for admins
  // stays visible but its rollout controls route here instead of writing, so
  // nobody turns on something still in development (see FeatureAccessRow).
  const [notReadyFeatureLabel, setNotReadyFeatureLabel] = useState<
    string | null
  >(null);
  // Feature + (Ready for admins, dev staff only) + one "On for all" column per
  // site + the accordion disclosure column, so the expanded detail cell spans
  // them all.
  const columnCount =
    (isDevStaff ? 2 : 1) + FEATURE_ACCESS_UI_ENVIRONMENTS.length + 1;

  // Feedback shows as a transient toast: auto-dismiss a few seconds after each
  // new message (each onFeedback call is a fresh object, so the timer resets).
  // Errors linger a little longer so the code/ref can be read; the manual
  // close stays available either way.
  useEffect(() => {
    if (!feedback) {
      return undefined;
    }
    const timeout = window.setTimeout(
      () => setFeedback(null),
      feedback.tone === "danger" ? 8000 : 4000,
    );
    return () => window.clearTimeout(timeout);
  }, [feedback]);

  return (
    <div className="vnext-stack">
      {feedback ? (
        <div className="vnext-feature-toast" role="status">
          <VNextNotice
            action={
              <button
                aria-label="Dismiss notification"
                className="vnext-notice__dismiss"
                onClick={() => setFeedback(null)}
                type="button"
              >
                <CloseIcon aria-hidden="true" fontSize="small" />
              </button>
            }
            tone={feedback.tone}
          >
            <div>{feedback.details.message}</div>
            {feedback.details.errorCode ? (
              <div>
                Code: <code>{feedback.details.errorCode}</code>
              </div>
            ) : null}
            {feedback.details.correlationId ? (
              <div>
                Ref: <code>{feedback.details.correlationId}</code>
              </div>
            ) : null}
          </VNextNotice>
        </div>
      ) : null}

      {features.length === 0 ? (
        <div className="vnext-empty">No features to manage.</div>
      ) : (
        <div className="vnext-scroll-panel">
          <table className="vnext-table">
            <thead>
              <tr>
                <th>Feature</th>
                {isDevStaff ? <th>Ready for admins</th> : null}
                {FEATURE_ACCESS_UI_ENVIRONMENTS.map(({ key, label }) => (
                  <th key={key}>{`On for all — ${label}`}</th>
                ))}
                <th>
                  <span className="vnext-sr-only">Expand feature</span>
                </th>
              </tr>
            </thead>
            {features.map((feature) => (
              <FeatureAccessRow
                canManage={canManage}
                columnCount={columnCount}
                feature={feature}
                isDevStaff={isDevStaff}
                key={feature.entitlementKey}
                onBlockedNotReady={setNotReadyFeatureLabel}
                onFeedback={setFeedback}
                onRequestConfirm={setConfirm}
                ready={isFeatureReady(feature, readiness)}
              />
            ))}
          </table>
        </div>
      )}

      <VNextDialog
        description="This affects every organisation."
        footer={
          <>
            <VNextButton onClick={() => setConfirm(null)} tone="quiet">
              Cancel
            </VNextButton>
            <VNextButton
              onClick={() => {
                confirm?.onConfirm();
                setConfirm(null);
              }}
              tone="primary"
            >
              {confirm?.confirmLabel ?? "Confirm"}
            </VNextButton>
          </>
        }
        onClose={() => setConfirm(null)}
        open={confirm !== null}
        title={confirm?.title ?? "Apply to all organisations"}
      >
        <div className="vnext-stack">
          <VNextNotice tone="warning">{confirm?.message}</VNextNotice>
        </div>
      </VNextDialog>

      <VNextDialog
        description="This feature is still in development."
        footer={
          <VNextButton
            onClick={() => setNotReadyFeatureLabel(null)}
            tone="primary"
          >
            Got it
          </VNextButton>
        }
        onClose={() => setNotReadyFeatureLabel(null)}
        open={notReadyFeatureLabel !== null}
        title="Not ready for release"
      >
        <div className="vnext-stack">
          <VNextNotice tone="warning">
            {notReadyFeatureLabel} is not ready for release yet, so it cannot be
            turned on for organisations. If you think it should go live, check
            in with the dev team.
          </VNextNotice>
        </div>
      </VNextDialog>
    </div>
  );
};

export default FeatureAccessTable;
